import { DestroyRef, Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { FeatureFlagName } from '@coin/shared/core/util-models';
import { FeatureFlagsService } from '@coin/shared/core/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * @example
 * // with else-template
 * <ng-container *coinFeatureFlag="'exampleFlag'; flagDisabledTemplate: oldState">
 *     <h1>NEW STATE<h1>
 * </ng-container>
 * <ng-template #oldState>
 * 	<h1>OLD STATE</h1>
 * </ng-template>
 * @example
 * // without else-template
 * <ng-container *coinFeatureFlag="'exampleFlag'">
 *     <h1>NEW STATE<h1>
 * </ng-container>
 */
@Directive({
  selector: '[coinFeatureFlag]',
  standalone: true
})
export class FeatureFlagDirective implements OnInit, OnDestroy {
  private templateRef = inject<TemplateRef<unknown>>(TemplateRef);
  private viewContainerRef = inject(ViewContainerRef);
  private featureFlagService = inject(FeatureFlagsService);
  private destroyRef = inject(DestroyRef);

  @Input() coinFeatureFlag: FeatureFlagName;

  // Intended duplication in Input-Name because Angular works like this
  @Input('coinFeatureFlagFlagDisabledTemplate') flagDisabledTemplate?: TemplateRef<unknown>;

  private lastFlagState: boolean;
  private subscription: Subscription;

  ngOnInit(): void {
    this.subscribeToFlagChanges();
    this.checkFeatureFlag();
  }

  private subscribeToFlagChanges(): void {
    this.subscription = this.featureFlagService.flagHasChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkFeatureFlag();
      });
  }

  private checkFeatureFlag(): void {
    if (this.featureFlagService.isActive(this.coinFeatureFlag) === this.lastFlagState) {
      return;
    }
    this.viewContainerRef.clear();
    if (this.featureFlagService.isActive(this.coinFeatureFlag)) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
    } else if (this.flagDisabledTemplate) {
      this.viewContainerRef.createEmbeddedView(this.flagDisabledTemplate);
    }
    this.lastFlagState = this.featureFlagService.isActive(this.coinFeatureFlag);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
